import { useState } from "react";
import { Button, PlatformIcon, DialogCancel } from "@cntrl/ui";
import type { components } from "@cntrl/api-client";
import { useReplaceDspLinks } from "../../app/hooks/useRelease.js";
import { errorMessage } from "../../app/hooks/apiError.js";
import { Modal } from "./Modal.js";
import { DSPS, dspDef } from "./dsp.js";
import styles from "./AssetEditorModal.module.scss";
import own from "./DspResolveModal.module.scss";

type DspLinkDto = components["schemas"]["DspLinkDto"];
type DspCandidateGroup = components["schemas"]["DspCandidateGroup"];

// Resolve DSP links (ART-219) — the human half of auto-populated smart-link buttons. The songstats-release sync
// auto-fills every DSP that resolved to ONE URL; a DSP that listed the release's recording twice on one
// storefront can't be auto-picked, so it lands here as a `dspCandidateGroup`. The manager taps the right one.
//
// The pick is written through the ORDINARY full-replace `PUT dsp-links` (useReplaceDspLinks): we merge the
// current links (kept) with the new picks and send the whole set, which the server stores as `confirmed` rows —
// so a resolved DSP drops off the candidate list at once, and the next sync never overwrites the human's choice.

export interface DspResolveModalProps {
  releaseId: string;
  version: number;
  currentLinks: DspLinkDto[];
  candidates: DspCandidateGroup[];
  onClose: () => void;
}

export function DspResolveModal({ releaseId, version, currentLinks, candidates, onClose }: DspResolveModalProps) {
  // silent: this modal renders its own inline error, so no global toast.
  const replaceDsp = useReplaceDspLinks(releaseId, { silent: true });
  const [picks, setPicks] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const anyPicked = Object.keys(picks).length > 0;

  function handleClose() {
    if (saving) return;
    onClose();
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      // Start from what the release already holds, apply the picks on top, then send the whole set in the
      // canonical DSP order — the same full-replace shape the editor uses.
      const chosen: Record<string, string> = {};
      for (const l of currentLinks) if (l.dsp && l.url) chosen[l.dsp] = l.url;
      for (const [dsp, url] of Object.entries(picks)) chosen[dsp] = url;

      const links = DSPS.map((d, i) => ({ dsp: d.key, url: chosen[d.key] ?? "", position: i })).filter((l) => l.url);
      await replaceDsp.mutateAsync({ version, body: { links } });
      onClose();
    } catch (e) {
      setError(errorMessage(e, "Couldn't save the links"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title="Resolve streaming links" onClose={handleClose}>
      <div className={styles.body}>
        <p className={styles.label}>Multiple possible links found. Select the correct link for this release.</p>

        <div className={own.groups}>
          {candidates.map((group) => {
            const def = dspDef(group.dsp);
            const urls = group.urls ?? [];
            return (
              <fieldset key={group.dsp ?? ""} className={own.group}>
                <legend className={own.groupHead}>
                  <span className={own.groupIcon} aria-hidden="true">
                    <PlatformIcon platform={def.key} size={16} />
                  </span>
                  {def.label}
                </legend>
                <div className={own.options}>
                  {urls.map((url) => {
                    const selected = picks[group.dsp ?? ""] === url;
                    return (
                      <label
                        key={url}
                        className={selected ? `${own.option} ${own.optionSelected}` : own.option}
                      >
                        <input
                          type="radio"
                          name={`dsp-${group.dsp}`}
                          value={url}
                          checked={selected}
                          disabled={saving}
                          onChange={() => setPicks((p) => ({ ...p, [group.dsp ?? ""]: url }))}
                        />
                        {/* A link inside the label: activating it opens the page (to check it is the right one) and
                            does not toggle the radio — the rest of the row does. */}
                        <a className={own.optionUrl} href={url} target="_blank" rel="noopener noreferrer">
                          {url}
                        </a>
                      </label>
                    );
                  })}
                </div>
              </fieldset>
            );
          })}
        </div>

        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}

        <div className={styles.actions}>
          <div className={styles.actionsRight}>
            <DialogCancel size="sm" disabled={saving} />
            <Button size="sm" onClick={save} loading={saving} disabled={!anyPicked}>
              Save links
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
